if (!customElements.get('rc-concept-film')) {
  customElements.define('rc-concept-film', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.holder = this.querySelector('[data-rc-film-player]');
      this.button = this.querySelector('[data-rc-film-control]');
      this.label = this.querySelector('[data-rc-film-label]');
      this.icon = this.querySelector('[data-rc-film-icon]');
      if (!this.holder || !this.button || this.dataset.videoEnabled !== 'true') return;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desired = !this.motion.matches && !navigator.connection?.saveData;
      this.onMessage = event => this.receive(event);
      this.onClick = () => this.toggle();
      this.onResize = () => this.resize();
      this.onScreen = () => this.select();
      this.onMotion = () => {
        if (this.motion.matches) {
          this.desired = false;
          this.pause();
        }
      };
      this.onVisibility = () => {
        if (document.hidden) this.pause();
        else if (this.desired) this.play();
      };
      window.addEventListener('message', this.onMessage);
      window.addEventListener('resize', this.onResize);
      this.mobile.addEventListener('change', this.onScreen);
      this.motion.addEventListener('change', this.onMotion);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.button.addEventListener('click', this.onClick);
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this);
      }
      this.select();
    }

    disconnectedCallback() {
      if (!this.onMessage) return;
      window.removeEventListener('message', this.onMessage);
      window.removeEventListener('resize', this.onResize);
      this.mobile.removeEventListener('change', this.onScreen);
      this.motion.removeEventListener('change', this.onMotion);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.button.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
      this.clear();
      this.initialized = false;
    }

    select() {
      this.clear();
      const screen = this.mobile.matches ? 'mobile' : 'desktop';
      this.source = this.querySelector(`[data-rc-film-template="${screen}"]`);
      const media = this.source?.content.querySelector('video, iframe');
      this.button.hidden = !media;
      this.state(false);
      if (media && this.desired && !document.hidden) this.mount();
    }

    mount() {
      if (this.media || !this.source) return;
      const media = this.source.content.querySelector('video, iframe')?.cloneNode(true);
      if (!media) return;
      this.ratio = Number(this.source.dataset.ratio) || 16 / 9;
      if (media.tagName === 'IFRAME') {
        let url;
        try { url = new URL(media.dataset.src); } catch { this.fail(); return; }
        if (url.protocol !== 'https:' || url.hostname !== 'player.vimeo.com' || !/^\/video\/\d+$/.test(url.pathname)) {
          this.fail();
          return;
        }
        for (const [key, value] of Object.entries({ background: '1', autoplay: this.desired ? '1' : '0', muted: '1', loop: '1', autopause: '0' })) url.searchParams.set(key, value);
        media.src = url.href;
        media.removeAttribute('data-src');
        media.addEventListener('load', () => { if (this.media === media) this.send('ping'); });
        media.addEventListener('error', () => { if (this.media === media) this.fail(); });
      } else {
        media.muted = true;
        media.playsInline = true;
        media.loop = true;
        media.addEventListener('playing', () => { if (this.media === media) this.started(); });
        media.addEventListener('pause', () => { if (this.media === media) this.state(false); });
        media.addEventListener('error', () => { if (this.media === media) this.fail(); });
      }
      this.media = media;
      this.holder.replaceChildren(media);
      this.resize();
      this.timeout = window.setTimeout(() => {
        if (!this.classList.contains('has-played')) this.fail();
      }, 15000);
      if (media.tagName === 'VIDEO') {
        media.load();
        this.play();
      }
    }

    send(method, value) {
      if (this.media?.tagName !== 'IFRAME') return;
      const message = value === undefined ? { method } : { method, value };
      this.media.contentWindow?.postMessage(message, 'https://player.vimeo.com');
    }

    receive(event) {
      if (this.media?.tagName !== 'IFRAME' || event.origin !== 'https://player.vimeo.com' || event.source !== this.media.contentWindow) return;
      let data = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || typeof data !== 'object') return;
      if (data.event === 'ready' || data.method === 'ping') {
        if (!this.ready) {
          this.ready = true;
          for (const name of ['playing', 'play', 'pause', 'error']) this.send('addEventListener', name);
          this.send('setMuted', true);
          this.send('setLoop', true);
        }
        if (this.desired && !document.hidden) this.play();
        else this.pause();
      } else if (data.event === 'playing' || data.event === 'play') {
        if (this.desired && !document.hidden) this.started();
        else this.pause();
      } else if (data.event === 'pause') {
        this.state(false);
      } else if (data.event === 'error') {
        if (data.data?.name === 'NotAllowedError' || data.data?.name === 'AbortError') {
          window.clearTimeout(this.timeout);
          this.desired = false;
          this.state(false);
        } else this.fail();
      }
    }

    started() {
      window.clearTimeout(this.timeout);
      this.classList.add('has-played');
      this.state(true);
    }

    state(playing) {
      this.playing = playing;
      this.label.textContent = playing ? this.button.dataset.pauseLabel : this.button.dataset.playLabel;
      this.icon.textContent = playing ? 'Ⅱ' : '▶';
    }

    toggle() {
      this.desired = !this.playing;
      if (this.desired) {
        this.mount();
        this.play();
      } else this.pause();
    }

    play() {
      if (!this.media || !this.desired || document.hidden) return;
      if (this.media.tagName === 'IFRAME') {
        if (this.ready) this.send('play');
      } else {
        const media = this.media;
        const attempt = media.play();
        attempt?.catch(() => {
          if (this.media !== media) return;
          window.clearTimeout(this.timeout);
          this.desired = false;
          this.state(false);
        });
      }
    }

    pause() {
      window.clearTimeout(this.timeout);
      if (this.media?.tagName === 'VIDEO') this.media.pause();
      else this.send('pause');
      this.state(false);
    }

    resize() {
      if (this.media?.tagName !== 'IFRAME') return;
      const { width, height } = this.getBoundingClientRect();
      this.media.style.width = `${Math.max(width, height * this.ratio)}px`;
      this.media.style.height = `${Math.max(height, width / this.ratio)}px`;
    }

    clear() {
      window.clearTimeout(this.timeout);
      const media = this.media;
      this.media = null;
      if (media?.tagName === 'VIDEO') media.pause();
      this.holder.replaceChildren();
      this.ready = false;
      this.classList.remove('has-played');
    }

    fail() {
      this.clear();
      this.desired = false;
      this.state(false);
      this.button.hidden = true;
    }
  });
}
